import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { DateRange, MatDatepickerModule } from '@angular/material/datepicker';
import { TranslateModule } from '@ngx-translate/core';
import * as moment from 'moment';
import { setTimeToMidnight } from 'src/app/utilities/date-utils';
import { MatCalendar } from '@angular/material/datepicker';
import { PencilButtonComponent } from '../pencil-button/pencil-button.component';
import { PencilIconComponent } from '../pencil-icon/pencil-icon.component';
import {
  DatepickerRangeName,
  DateRangePickerConstantsService,
} from '../../../services/date-range-picker-constants.service';

export interface DatepickerRange {
  name: string;
  range: DateRange<Date | null>;
  localizedLabel?: string;
}

export interface DateRangePickerAttributes {
  readonly templateRef: TemplateRef<any>;
  readonly applyButtonClicked: EventEmitter<DatepickerRange>;
  readonly cancelButtonClicked: EventEmitter<DatepickerRange>;
  readonly resetPendingSelectionRange: () => void;
}

@Component({
  selector: 'app-date-range-picker',
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss'],
  standalone: true,
  imports: [
    PencilButtonComponent,
    MatDatepickerModule,
    TranslateModule,
    PencilIconComponent,
    CommonModule,
  ],
})
export class DateRangePickerComponent implements DateRangePickerAttributes {
  readonly keepInsertionOrderComparator = () => 0;

  @ViewChild(TemplateRef) templateRef!: TemplateRef<any>;
  @ViewChild(MatCalendar) calendar!: MatCalendar<Date>;

  /**
   * Maximum allowed date range in days.
   * Set to null to disable the maximum range restriction.
   * Default is 90 days.
   */
  @Input() maxDateRangeDays: number | null = 90;

  protected todayDate = new Date();

  datepickerRanges: Map<string, DatepickerRange> =
    this.dateRangePickerConstantsService.DEFAULT_DATEPICKER_RANGES;

  selectedRange: DatepickerRange = {
    ...this.dateRangePickerConstantsService.DEFAULT_INITIAL_DATEPICKER_RANGE!,
  };
  pendingSelectionRange: DatepickerRange = {
    ...this.dateRangePickerConstantsService.DEFAULT_INITIAL_DATEPICKER_RANGE!,
  };

  @Output() applyButtonClicked = new EventEmitter<DatepickerRange>();
  @Output() cancelButtonClicked = new EventEmitter<DatepickerRange>();

  customRange = this.datepickerRanges.get(DatepickerRangeName.CUSTOM);

  constructor(private dateRangePickerConstantsService: DateRangePickerConstantsService) {}

  /**
   * Filter function for the calendar to limit date selection to maxDateRangeDays
   * Only applies restrictions when start date is selected but end date is not
   */
  dateFilter = (date: Date): boolean => {
    // If maxDateRangeDays is set and we have a start date but no end date yet, restrict selection to +/- maxDateRangeDays
    if (
      this.maxDateRangeDays &&
      this.pendingSelectionRange.range.start &&
      !this.pendingSelectionRange.range.end
    ) {
      const startDate = moment(this.pendingSelectionRange.range.start);
      const currentDate = moment(date);
      const daysDiff = Math.abs(currentDate.diff(startDate, 'days'));

      // Allow dates within the maxDateRangeDays limit
      return daysDiff <= this.maxDateRangeDays;
    }

    // Otherwise, allow all dates
    return true;
  };

  onCalendarDayClick(date: Date) {
    if (
      this.pendingSelectionRange.range.start &&
      date >= this.pendingSelectionRange.range.start &&
      !this.pendingSelectionRange.range.end
    ) {
      this.pendingSelectionRange.range = new DateRange(
        this.pendingSelectionRange.range.start,
        date,
      );
    } else {
      this.pendingSelectionRange.range = new DateRange(date, null);
    }

    // If start, end of activeDatepicker range matches any of
    // the pre-defined datepickerRanges find the matching datepickerRange

    const matchingDatepickerRange = Array.from(this.datepickerRanges.values()).find((selection) => {
      if (
        selection.range.start &&
        selection.range.end &&
        setTimeToMidnight(selection.range.start).getTime() ===
          setTimeToMidnight(this.pendingSelectionRange.range.start!).getTime() &&
        setTimeToMidnight(selection.range.end).getTime() ===
          setTimeToMidnight(this.pendingSelectionRange.range.end!).getTime()
      ) {
        return selection;
      }
    });

    // If pre-defined dates don't match the selected date then set the name of active range to custom
    this.pendingSelectionRange.name = matchingDatepickerRange?.name || DatepickerRangeName.CUSTOM;
    if (this.pendingSelectionRange.name === DatepickerRangeName.CUSTOM && this.customRange) {
      this.customRange.range = this.pendingSelectionRange.range;
    }
  }

  onDatepickerRangeClick(clickedSelection: DatepickerRange) {
    this.pendingSelectionRange = { ...clickedSelection };

    // Navigate to the relevant month when a preset range is selected
    // Show the start date's month
    if (this.calendar && clickedSelection.range.start) {
      this.calendar.activeDate = clickedSelection.range.start;
    }
  }

  cancelClicked() {
    this.cancelButtonClicked.emit();
    this.resetPendingSelectionRange();
  }

  applyClicked() {
    this.selectedRange = { ...this.pendingSelectionRange };
    this.applyButtonClicked.emit({
      name: this.pendingSelectionRange.name,
      range: new DateRange(
        moment(this.pendingSelectionRange.range.start!).startOf('day').toDate(),
        moment(this.pendingSelectionRange.range.end!).endOf('day').toDate(),
      ),
    });
  }

  resetPendingSelectionRange() {
    this.pendingSelectionRange = { ...this.selectedRange };
  }
}
